import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonPinIcon from '@mui/icons-material/PersonPin'; // Icon for Coach
import SchoolIcon from '@mui/icons-material/School'; // Icon for Host/Teacher
import CheckIcon from '@mui/icons-material/Check';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'; // Import new icon
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const WhyMe = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const content = translations[language].over.whyMe;
  const t = translations[language];

  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  useEffect(() => {
    const hash = location.hash.substring(1);
    if (hash === 'coach' || hash === 'host') {
      setExpandedPanel(hash);
    } else {
      setExpandedPanel('coach');
    }
  }, [location.hash]);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const listItemStyle = {
    mb: 1.5,
    display: 'flex',
    alignItems: 'flex-start',
    pl: 0,
  };

  const checkIconStyle = {
    color: 'primary.main',
    mr: 1.5,
    mt: 0.5,
    fontSize: '1.2rem'
  };

  const accordionStyle = {
    boxShadow: 'none',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderRadius: '12px',
    overflow: 'hidden',
    mb: 3,
    '&:before': {
        display: 'none',
    },
    '&.Mui-expanded': {
        margin: '0 0 24px 0',
    },
    '&:last-of-type': {
        marginBottom: 0,
         '&.Mui-expanded': {
             marginBottom: 0,
         }
    }
  };

  const renderSection = (sectionContent: any, ListIconComponent: React.ElementType) => (
    <>
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.dark', mb: 2, fontWeight: 500 }}>
          {sectionContent.title} 
      </Typography>
      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
        {sectionContent.intro}
      </Typography>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
        {sectionContent.approachTitle}
      </Typography>
      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
        {sectionContent.approachText}
      </Typography>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
        {sectionContent.expectTitle}
      </Typography>
      <List sx={{ pl: 0 }}>
        {sectionContent.expectItems && Array.isArray(sectionContent.expectItems) ? 
          sectionContent.expectItems.map((item: any, index: number) => (
            <ListItem key={index} disablePadding sx={listItemStyle}>
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <ListIconComponent sx={checkIconStyle} />
              </ListItemIcon>
              <ListItemText primary={typeof item === 'string' ? item : (item && item.text ? item.text : '')} sx={{ color: 'text.secondary' }}/>
            </ListItem>
          )) : null
        }
      </List>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
        {sectionContent.promiseTitle}
      </Typography>
      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
        {sectionContent.promiseText}
      </Typography>
    </>
  );

  // Custom content for coach section when standard content might not be available
  const renderCoachContent = () => (
    <>
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.dark', mb: 2, fontWeight: 500 }}>
        {language === 'nl' ? 'Waarom ik als jouw Carrièrecoach?' : 'Why choose me as your Career Coach?'}
      </Typography>
      
      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
        {language === 'nl' 
          ? 'Zoek je niet gewoon een (nieuwe) baan, maar een carrière waar je elke dag vol plezier naartoe gaat? Waar je talenten tot bloei komen en waar je écht impact maakt? Als jouw carrièrecoach help ik jou te ontdekken waar jij gelukkig van wordt en geef jou de loopbaanbegeleiding waarmee je dit kan bereiken.'
          : 'Are you looking for more than just a (new) job, but a career that you enjoy going to every day? Where your talents flourish and where you make a real impact? As your career coach, I help you discover what makes you happy and provide the career guidance to achieve it.'}
      </Typography>
      
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
        {language === 'nl' ? 'Wat mijn aanpak uniek maakt' : 'What makes my approach unique'}
      </Typography>
      
      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
        {language === 'nl'
          ? 'Waar andere coaches blijven steken in algemene adviezen, duik ik graag dieper. Door mijn vermogen om snel vertrouwen op te bouwen en een persoonlijke band met mijn cliënten te creëren komen we snel tot de kern van wat jij werkelijk zoekt in je loopbaan. Ik ben oprecht geïnteresseerd in jouw succes en breng een positieve, motiverende aanpak naar onze sessies. Hierin focus ik op jouw sterke punten en zorg altijd voor een veilige, ondersteunende sfeer waarin je open en eerlijk kunt zijn. Ik ben de coach die onvoorwaardelijk gelooft in jouw potentieel, zelfs wanneer jij dat even niet doet.'
          : 'Where other coaches stick to general advice, I like to dive deeper. Through my ability to quickly build trust and create a personal connection with my clients, we quickly get to the core of what you are truly looking for in your career. I am genuinely interested in your success and bring a positive, motivating approach to our sessions. I focus on your strengths and always provide a safe, supportive atmosphere where you can be open and honest. I am the coach who unconditionally believes in your potential, even when you momentarily don\'t.'}
      </Typography>
      
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
        {language === 'nl' ? 'Jouw reis, professioneel begeleid' : 'Your journey, professionally guided'}
      </Typography>
      
      <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
        {language === 'nl'
          ? 'Of je nu een student bent die aan de start staat van je professionele loopbaan of een young professional die klaar is voor de volgende stap – mijn loopbaancoaching is volledig afgestemd op jouw situatie. Samen ontdekken we welke carrière het beste bij jou past. Ik bied je de begeleiding en ondersteuning die je nodig hebt om je ware potentieel te ontdekken en geef je het vertrouwen dat je nodig hebt om succes te behalen.'
          : 'Whether you are a student at the beginning of your professional career or a young professional ready for the next step – my career coaching is completely tailored to your situation. Together we discover which career suits you best. I offer you the guidance and support you need to discover your true potential and give you the confidence you need to achieve success.'}
      </Typography>
      
      {content.coach && content.coach.expectItems && Array.isArray(content.coach.expectItems) && (
        <>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
            {language === 'nl' ? 'Wat je van mij kunt verwachten:' : 'What you can expect from me:'}
          </Typography>
          
          <List sx={{ pl: 0 }}>
            {content.coach.expectItems.map((item: any, index: number) => (
              <ListItem key={index} disablePadding sx={listItemStyle}>
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  <LightbulbOutlinedIcon sx={checkIconStyle} />
                </ListItemIcon>
                <ListItemText primary={typeof item === 'string' ? item : (item && item.text ? item.text : '')} sx={{ color: 'text.secondary' }}/>
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      {content.coach && content.coach.promiseTitle && (
        <>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
            {content.coach.promiseTitle}
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
            {content.coach.promiseText}
          </Typography>
        </>
      )}
    </>
  );

  return (
    <Box>
      <Accordion 
        sx={accordionStyle} 
        expanded={expandedPanel === 'coach'} 
        onChange={handleAccordionChange('coach')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="coach-content"
          id="coach-header"
          sx={{ 
            minHeight: 64,
            bgcolor: alpha(theme.palette.primary.light, 0.05),
            '& .MuiAccordionSummary-content': { 
                alignItems: 'center', 
                gap: 1.5 
            }
          }}
        >
          <PersonPinIcon sx={{ color: 'primary.main' }} /> 
          <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 500 }}>
            {language === 'nl' ? 'Als Carrièrecoach' : 'As Career Coach'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 2, md: 3 } }}>
          {/* Use the custom render function that ensures the compelling content is shown */}
          {renderCoachContent()}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
             <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                {language === 'nl' ? 'Klaar om de volgende stap te zetten?' : 'Ready to take the next step?'}
             </Typography>
             <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', maxWidth: '500px', mx: 'auto' }}>
                 {language === 'nl' ? 'Plan een vrijblijvend kennismakingsgesprek om te zien hoe ik je als coach kan helpen.' : 'Schedule a free consultation to see how I can help you as a coach.'}
             </Typography>
             <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => navigate('/contact', { state: { subject: 'Afspraak maken coaching' } })}
                sx={{
                  py: 1.2,
                  px: 4,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  borderRadius: '8px'
                }}
              >
                 {'Maak een afspraak'}
              </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion 
        sx={accordionStyle} 
        expanded={expandedPanel === 'host'} 
        onChange={handleAccordionChange('host')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="host-content"
          id="host-header"
           sx={{ 
            minHeight: 64,
            bgcolor: alpha(theme.palette.primary.light, 0.05),
            '& .MuiAccordionSummary-content': { 
                alignItems: 'center', 
                gap: 1.5 
            }
          }}
        >
          <SchoolIcon sx={{ color: 'primary.main' }} /> 
          <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 500 }}>
            {language === 'nl' ? 'Als Workshop Host/Docent' : 'As Workshop Host/Teacher'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 2, md: 3 } }}>
          {t.over.whyHost && (
            <>
              <Typography variant="h5" gutterBottom sx={{ color: 'primary.dark', mb: 2, fontWeight: 500 }}>
                {t.over.whyHost.title}
              </Typography>
              <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
                {t.over.whyHost.intro}
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
                {t.over.whyHost.approachTitle}
              </Typography>
              <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
                {t.over.whyHost.approachText}
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
                {t.over.whyHost.expectTitle}
              </Typography>
              <List sx={{ pl: 0 }}>
                {t.over.whyHost.expectItems?.map((item: any, index: number) => (
                  <ListItem key={index} disablePadding sx={listItemStyle}>
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      <CheckIcon sx={checkIconStyle} />
                    </ListItemIcon>
                    <ListItemText primary={item.text} sx={{ color: 'text.secondary' }}/>
                  </ListItem>
                ))}
              </List>
              
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
                {t.over.whyHost.promiseTitle}
              </Typography>
              <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
                {t.over.whyHost.promiseText}
              </Typography>
              
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                    {t.over.whyHost.ctaTitle || (language === 'nl' ? 'Klaar om impact te maken?' : 'Ready to make an impact?')} 
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => navigate('/contact', { state: { subject: 'Workshop/Spreker Boeken' } })}
                  sx={{
                    py: 1.2,
                    px: 4,
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    borderRadius: '8px'
                  }}
                >
                  {t.over.whyHost.ctaButton || (language === 'nl' ? 'Neem contact op' : 'Contact me')}
                </Button>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default WhyMe; 